import { useState, useEffect } from 'react';
import { Trash2, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DanceWarmupsAdmin() {
  const [warmups, setWarmups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [warmupType, setWarmupType] = useState('spotify'); // 'spotify' or 'mp3'
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchWarmups();
  }, []);

  const fetchWarmups = async () => {
    try {
      const response = await axios.get(`${API}/dance-warmups`);
      setWarmups(response.data);
    } catch (error) {
      console.error('Error fetching dance warmups:', error);
      toast.error('Failed to load dance warmups');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('adminToken');
      
      const formData = new FormData();
      formData.append('name', newName);
      formData.append('type', warmupType);
      
      if (warmupType === 'spotify') {
        if (!newUrl) {
          toast.error('Please enter a Spotify URL');
          setSubmitting(false);
          return;
        }
        formData.append('songUrl', newUrl);
      } else {
        if (!newFile) {
          toast.error('Please select an MP3 file');
          setSubmitting(false);
          return;
        }
        formData.append('mp3File', newFile);
      }
      
      console.log('Adding dance warmup:', { name: newName, type: warmupType });
      
      const response = await axios.post(`${API}/dance-warmups`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Dance warmup added successfully:', response.data);
      toast.success('Dance warmup added!');
      setNewName('');
      setNewUrl('');
      setNewFile(null);
      setWarmupType('spotify');
      setShowAddForm(false);
      fetchWarmups();
    } catch (error) {
      console.error('Error adding dance warmup:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to add dance warmup');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/dance-warmups/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Dance warmup deleted');
      fetchWarmups();
    } catch (error) {
      console.error('Error deleting dance warmup:', error);
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold">Dance Warmups</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Add Song
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAdd} className="glass-card p-4 space-y-3">
          <div>
            <label className="block text-sm font-semibold mb-1">Song Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., Uptown Funk"
              className="input-field w-full"
              required
            />
          </div>
          
          {/* Type Selector */}
          <div>
            <label className="block text-sm font-semibold mb-2">Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="warmupType"
                  value="spotify"
                  checked={warmupType === 'spotify'}
                  onChange={(e) => setWarmupType(e.target.value)}
                  className="w-4 h-4 cursor-pointer"
                />
                <span>Spotify Link</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="warmupType"
                  value="mp3"
                  checked={warmupType === 'mp3'}
                  onChange={(e) => setWarmupType(e.target.value)}
                  className="w-4 h-4 cursor-pointer"
                />
                <span>Upload MP3</span>
              </label>
            </div>
          </div>

          {warmupType === 'spotify' ? (
            <div>
              <label className="block text-sm font-semibold mb-1">
                Spotify Song URL
              </label>
              <input
                type="text"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="spotify:track:... or https://open.spotify.com/track/..."
                className="input-field w-full"
                required={warmupType === 'spotify'}
              />
              <p className="text-xs mt-1 opacity-70">
                Paste Spotify song link or URI
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold mb-1">
                MP3 File
              </label>
              <input
                type="file"
                accept="audio/mpeg,audio/mp3"
                onChange={(e) => setNewFile(e.target.files[0])}
                className="input-field w-full"
                required={warmupType === 'mp3'}
              />
              <p className="text-xs mt-1 opacity-70">
                Upload an MP3 file for this warmup
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setNewName('');
                setNewUrl('');
                setNewFile(null);
                setWarmupType('spotify');
              }}
              className="flex-1 bg-white/10 hover:bg-white/20 py-2 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 btn-primary disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add Song'}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {warmups.length === 0 ? (
          <p className="text-center opacity-70 py-4">
            No dance warmup songs yet. Add your first song!
          </p>
        ) : (
          warmups.map(warmup => (
            <div
              key={warmup.id}
              className="glass-card p-3 flex justify-between items-center"
            >
              <div>
                <p className="font-semibold">{warmup.name}</p>
                <p className="text-xs opacity-70 truncate max-w-xs">
                  {warmup.songUrl}
                </p>
              </div>
              <button
                onClick={() => handleDelete(warmup.id, warmup.name)}
                className="bg-red-500 hover:bg-red-600 p-2 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
